import { IRegEdSubmissionMetadata } from '@Queries/RegEdCompliance';
import * as Api from '@ViewModels';
import { UseMutationResult } from '@tanstack/react-query';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import moment from 'moment';
import React from 'react';
import { dedupeByProvider } from '../../../../extViewmodels/Utils';
import { SocialMediaPostStatusDetails } from '../../../../models';
import { CampaignType } from '../../../../models/AdminModels';
import { accountHasComplianceRequired, getStatusGroupOptions } from '../../../../models/UiUtils';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { ImpersonationContextViewModel } from '../../../../viewmodels/AdminViewModels';
import { AiButton } from '../../../components-v2/AiButton';
import { Button } from '../../../components-v2/Button';
import { ButtonGroup } from '../../../components-v2/ButtonGroup';
import { Dropdown, DropdownContent, DropdownItem, DropdownTrigger } from '../../../components-v2/Dropdown';
import { Collapsible } from '../../../components/Collapsible';
import { TextInput } from '../../../components/TextInput';
import { PopoverType, TinyPopover } from '../../../components/TinyPopover';
import { Toggle } from '../../../components/Toggle';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/Tooltip';
import { AdvancedOptionsButton } from '../../../components/automation/AutomationStepCard/presentation';
import { CampaignDetailsSummary } from '../../../components/campaigns/CampaignDetailsSummary';
import { RegEdComplianceApprovalPrompt } from '../../../components/campaigns/ComplianceApprovalPrompt/RegEdComplianceApprovalPrompt';
import { PreviewSocialMediaPost } from '../../../components/socialMedia/PreviewSocialMediaPost';
import { ChevronDown } from '../../../components/svgs/icons/ChevronDown';
import { DangerIcon } from '../../../components/svgs/icons/DangerIcon';
import { ExpiryIcon } from '../../../components/svgs/icons/ExpiryIcon';
import { InfoIcon } from '../../../components/svgs/icons/InfoIcon';
import { brandPrimary, charmGray, warningDark } from '../../../styles/colors';
import { bs } from '../../../styles/styles';
import { IEditSocialMediaPostContext } from './context';
import { styleSheet } from './styles';

interface SocialMediaNavigationProps {
	onAIContentGenerationAssistantButtonClicked: () => void;
	canSaveTemplate: boolean;
	updateTemplateMutation: UseMutationResult<
		unknown,
		Api.IOperationResultNoValue,
		{
			template: Api.ITemplate;
		},
		unknown
	>;
	handleSave: () => void;
	onSaveAsTemplateClicked: () => void;
	newPost: boolean;
	impersonationCtx: Api.IImpersonationContext;
	context: IEditSocialMediaPostContext;
	onSaveAsButtonClicked: () => void;
	source: Api.SocialMediaPostReportViewModel | Api.SocialMediaPostViewModel;
	onShowCancelConfirmation: () => void;
	onShowUpdateConfirmation: () => void;
}

export const SocialMediaNavigation = ({
	onAIContentGenerationAssistantButtonClicked,
	canSaveTemplate,
	updateTemplateMutation,
	handleSave,
	onSaveAsTemplateClicked,
	newPost,
	impersonationCtx,
	context,
	onSaveAsButtonClicked,
	source,
	onShowCancelConfirmation,
	onShowUpdateConfirmation,
}: SocialMediaNavigationProps) => {
	const userSession = useUserSession();

	const accountHasAiGeneratedContentFeatureEnabled = (
		impersonationCtx?.account ? impersonationCtx.account : userSession.account
	).features?.contentGeneration?.enabled;

	return (
		<div
			className={css(
				bs.flex,
				bs.boxBorder,
				bs.justifyBetween,
				bs.px4,
				bs.py2,
				bs.border0,
				bs.borderB,
				bs.borderSolid,
				bs.borderMentionDark,
				bs.itemsCenter
			)}
		>
			{accountHasAiGeneratedContentFeatureEnabled ? (
				<AiButton
					onClick={onAIContentGenerationAssistantButtonClicked}
					title='Write post with our AI assistant'
					size='xs'
				/>
			) : null}

			<div className={css(bs.flex, bs.itemsCenter, bs.gap2, bs.flex1, bs.justifyEnd)}>
				<ButtonGroup>
					{canSaveTemplate ? (
						<>
							<Button size='sm' variant='secondary' onClick={handleSave} disabled={updateTemplateMutation.isPending}>
								Save Template
							</Button>
							<Dropdown>
								<DropdownTrigger
									render={<Button size='sm' variant='secondary' disabled={!context.postContent?.length} />}
								>
									<ChevronDown />
								</DropdownTrigger>
								<DropdownContent align='end'>
									<DropdownItem onClick={onSaveAsTemplateClicked}>Save as</DropdownItem>
								</DropdownContent>
							</Dropdown>
						</>
					) : (
						<Button size='sm' onClick={onSaveAsTemplateClicked} disabled={!context.postContent?.length}>
							Save as
						</Button>
					)}
				</ButtonGroup>
				{newPost && !impersonationCtx && context?.selectedCreator?.id === userSession?.user?.id ? (
					<Button variant='secondary' size='sm' onClick={onSaveAsButtonClicked} disabled={!context.postContent?.length}>
						Save as draft
					</Button>
				) : null}
				{newPost && !impersonationCtx && context?.selectedCreator?.id !== userSession?.user?.id ? (
					<Tooltip delayDuration={0}>
						<TooltipTrigger>
							<InfoIcon className={css(bs.block)} fillColor={brandPrimary} />
						</TooltipTrigger>
						<TooltipContent>Draft posts can only be saved when selecting yourself as sender.</TooltipContent>
					</Tooltip>
				) : null}
				{source?.status === Api.PostStatus.Draft ? (
					<Button variant='destructive-secondary' size='sm' onClick={onShowCancelConfirmation}>
						<span>Delete</span>
					</Button>
				) : null}
				{source ? (
					<Button size='sm' onClick={onShowUpdateConfirmation} disabled={!context?.hasChanges}>
						<span>Save</span>
					</Button>
				) : null}
			</div>
		</div>
	);
};

interface StatusSpecificDetailsProps {
	postToEdit: Api.SocialMediaPostViewModel;
	scheduledPost: Api.SocialMediaPostViewModel;
	context: IEditSocialMediaPostContext;
	isComplianceFlow: boolean;
	sendWithComplianceEmail: string;
	setSendWithComplianceEmail: (value: string) => void;
	isSchedulingInProgress: boolean;
	impersonationCtx: Api.IImpersonationContext;
	handleScheduleForLater: () => void;
	handleScheduleWithoutCompliance: () => void;
	handlePostNow: () => void;
	checkComplianceEmail: () => boolean;
	lastUsedComplianceRecipientEmail: string;
	handleChangeDate: () => void;
	onViewCitationsClicked: () => void;
	source: Api.SocialMediaPostReportViewModel | Api.SocialMediaPostViewModel;
	/** Called after overlay logo toggle; used for auto-save in admin content calendar when Save button is hidden */
	onOverlayLogoChange?: () => void;
	usesRegEd?: boolean;
	onScheduleRegEdCompliance?: (e: React.MouseEvent<HTMLElement>, metadata: IRegEdSubmissionMetadata) => void;
	/** Last-submitted metadata — re-seeds the form when the composer remounts after the send-options round trip. */
	regEdMetadata?: IRegEdSubmissionMetadata | null;
}

export const StatusSpecificDetails = observer(
	({
		postToEdit,
		scheduledPost,
		context,
		isComplianceFlow,
		sendWithComplianceEmail,
		setSendWithComplianceEmail,
		isSchedulingInProgress,
		impersonationCtx,
		handleScheduleForLater,
		handleScheduleWithoutCompliance,
		handlePostNow,
		checkComplianceEmail,
		lastUsedComplianceRecipientEmail,
		handleChangeDate,
		onViewCitationsClicked,
		source,
		onOverlayLogoChange,
		usesRegEd,
		onScheduleRegEdCompliance,
		regEdMetadata,
	}: StatusSpecificDetailsProps) => {
		const userSession = useUserSession();
		const statusDetails = getStatusGroupOptions(postToEdit || scheduledPost);
		const account = impersonationCtx?.account || userSession.account;
		const socialMediaEnabled = account?.features?.socialMedia?.enabled;
		const complianceRequired = accountHasComplianceRequired(
			userSession,
			new ImpersonationContextViewModel(impersonationCtx)
		);

		const [advancedOptionsToggled, setAdvancedOptionsToggled] = React.useState(context.overlayLogo);

		const showAdvanceOptions =
			socialMediaEnabled &&
			context.postImages.length > 0 &&
			!context.postImages.some(x => x.hasLogoOverlay) &&
			statusDetails !== SocialMediaPostStatusDetails.CancelledOrFailed;

		const renderAdvancedOptionsTrigger = React.useMemo(() => {
			const handleToggle = (overlayLogo: boolean) => {
				context.setOverlayLogo(overlayLogo);

				if (!onOverlayLogoChange) {
					context.setHasChanges(true);
				}

				if (onOverlayLogoChange) {
					setTimeout(() => onOverlayLogoChange(), 0);
				}
			};

			return (
				<div className={css(styleSheet.advancedOptions)}>
					<AdvancedOptionsButton
						isCollapsed={!advancedOptionsToggled}
						onClick={() => setAdvancedOptionsToggled(!advancedOptionsToggled)}
					/>
					<Collapsible isCollapsed={!advancedOptionsToggled}>
						{!context.postImages.some(x => x.hasLogoOverlay) ? (
							<div className={css(bs.flex, bs.flexCenter, bs.gap2)}>
								<Toggle
									className={css(styleSheet.toggle)}
									id='logo-toggle'
									isOn={context.overlayLogo}
									text={!context.overlayLogo ? 'Overlay Logo Off' : 'Overlay Logo On'}
									onToggleCheckChanged={handleToggle}
									uncheckedColor='#B1B4B6'
								/>
								<TinyPopover
									align='end'
									anchor={<InfoIcon fillColor={charmGray} />}
									autoCloseOtherPopoversOnHover
									dismissOnOutsideAction
									anchorStyles={[styleSheet.infoPopoverToggle]}
									placement={['top', 'right']}
									toggleOnHover
									type={PopoverType.lightBlue}
								>
									<div className={css(styleSheet.popover)}>
										This will overlay your account&apos;s logo on the post&apos;s media at the moment the post gets
										posted. Click on the image preview to see the overlay.
									</div>
								</TinyPopover>
							</div>
						) : null}
						{context.overlayLogo ? (
							<span className={css(styleSheet.logoSizeLabel)}>
								Logo Size: {context.logoSize ?? Api.DEFAULT_LOGO_SIZE}px
							</span>
						) : null}
					</Collapsible>
				</div>
			);
		}, [advancedOptionsToggled, context, onOverlayLogoChange]);

		switch (statusDetails) {
			case SocialMediaPostStatusDetails.CancelledOrFailed:
				return (
					<>
						<div className={css(styleSheet.card)}>
							<div className={css(styleSheet.cardTitle)}>Send Time</div>
							<div className={css(styleSheet.cardActions)}>
								{scheduledPost ? (
									<time dateTime={scheduledPost.dueDate}>
										{moment(scheduledPost.dueDate).format('dddd, MMMM Do, h:mm A')}
									</time>
								) : (
									<time dateTime={source?.dueDate}>{moment(source?.dueDate).format('dddd, MMMM Do, h:mm A')}</time>
								)}
							</div>
						</div>
						{source?.status === Api.PostStatus.Cancelled ? (
							<div className={css(styleSheet.card)}>
								<div className={css(styleSheet.cardActions)}>
									<span className={css(styleSheet.cardSubSection)}>
										<DangerIcon />
										{context?.postTargets?.find(x => x.state?.status === Api.PostTargetStatus.Posted)
											? `Part of this post was cancelled on ${moment(source?.lastModifiedDate).format('MMMM Do, h:mm A')}`
											: `This post was cancelled on ${moment(source?.lastModifiedDate).format('MMM Do, h:mm A')}`}
									</span>
								</div>
							</div>
						) : null}
					</>
				);
			case SocialMediaPostStatusDetails.NewOrDraft:
				return (
					<>
						{source ? (
							<>
								<div className={css(styleSheet.card)}>
									<div className={css(styleSheet.cardTitle)}>Last Updated</div>
									<div className={css(styleSheet.cardActions)}>
										<time dateTime={source?.lastModifiedDate}>
											{moment(source?.lastModifiedDate).format('dddd, MMMM Do, h:mm A')}
										</time>
									</div>
								</div>
								{source?.actor && !source.actor.isLevitateSupport ? (
									<div className={css(styleSheet.card)}>
										<div className={css(styleSheet.cardTitle)}>Created By</div>
										<div className={css(styleSheet.cardActions)}>
											<span className={css(styleSheet.approvalStatusTitle)}>{source?.actor?.name}</span>
										</div>
									</div>
								) : null}
							</>
						) : null}
						<div
							className={css(styleSheet.ctaContainer, usesRegEd && styleSheet.ctaContainerRegEd)}
							data-reged-host={usesRegEd ? '' : undefined}
						>
							<div className={css(styleSheet.ctaContainerButtons, usesRegEd && styleSheet.ctaContainerButtonsRegEd)}>
								{usesRegEd && onScheduleRegEdCompliance ? (
									<RegEdComplianceApprovalPrompt
										impersonationContext={impersonationCtx}
										campaignType={CampaignType.Social}
										socialProviders={context.postTargets?.map(target => target.provider)}
										collapsible
										disabled={!context.postTargets?.length || !context.postContent?.length}
										isLoading={isSchedulingInProgress}
										initialFormat={regEdMetadata?.format}
										initialAnswers={regEdMetadata?.answers}
										onScheduleRegEdCompliance={onScheduleRegEdCompliance}
										styles={[styleSheet.regEdPromptInSidebar]}
									/>
								) : (
									<>
										{isComplianceFlow && (
											<TextInput
												inputId='user-emailAddress'
												placeholder='Please enter Compliance Email'
												type='text'
												onBlur={() => {
													if (!checkComplianceEmail()) {
														return;
													}
													localStorage.setItem(lastUsedComplianceRecipientEmail, sendWithComplianceEmail);
												}}
												onChange={ev => {
													setSendWithComplianceEmail(ev.target.value);
												}}
												className={css(styleSheet.complianceInput)}
												value={sendWithComplianceEmail}
											/>
										)}
										<button
											className={css(bs.ctaButton)}
											disabled={!context.postTargets?.length || !context.postContent?.length || isSchedulingInProgress}
											onClick={handleScheduleForLater}
											style={{ marginBottom: 5 }}
										>
											<span>{isComplianceFlow ? 'Schedule Post with Compliance Pending' : 'Schedule for later'}</span>
										</button>
									</>
								)}
								{!impersonationCtx ? (
									<>
										{!usesRegEd && isComplianceFlow && !complianceRequired ? (
											// Only show this button if we are in the compliance flow and it is not required.
											// It's intentional that isComplianceFlow and complianceRequired are evaluated separately.

											<button
												className={css(bs.ctaButtonReverse)}
												disabled={
													!context.postTargets?.length || !context.postContent?.length || isSchedulingInProgress
												}
												onClick={handleScheduleWithoutCompliance}
												style={{ marginBottom: 5 }}
											>
												<span>Schedule without Compliance</span>
											</button>
										) : null}
										{!usesRegEd && userSession?.user?.id === context.selectedCreator?.id ? (
											// RegEd requires compliance, so immediate publish (which would bypass the filing) is hidden.
											<button
												className={css(bs.ctaButtonReverse)}
												disabled={
													!context.postTargets?.length || !context.postContent?.length || isSchedulingInProgress
												}
												onClick={handlePostNow}
												style={{ marginBottom: 5 }}
											>
												<span>Post Now</span>
											</button>
										) : null}
									</>
								) : null}
							</div>
							{showAdvanceOptions ? renderAdvancedOptionsTrigger : null}
						</div>
					</>
				);
			case SocialMediaPostStatusDetails.NotCancelledFailedDraftOrUnknown:
				return (
					<>
						<CampaignDetailsSummary
							campaign={scheduledPost}
							onChangeScheduleClicked={handleChangeDate}
							disabled={context?.hasChanges}
							onViewCitationsClicked={onViewCitationsClicked}
						/>
						{source?.status === Api.PostStatus.PartiallySucceeded || source?.status === Api.PostStatus.Started ? (
							<div className={css(styleSheet.card)}>
								<div className={css(styleSheet.cardActions)}>
									<span className={css(styleSheet.cardSubSection)}>
										<DangerIcon />
										{`Some targets for this post ${
											source?.status === Api.PostStatus.PartiallySucceeded ? 'were rejected.' : 'are still in progress.'
										}`}
									</span>
								</div>
							</div>
						) : null}
						{showAdvanceOptions ? renderAdvancedOptionsTrigger : null}
					</>
				);
			default:
				return;
		}
	}
);

interface DefaultComposerContextProps {
	context: IEditSocialMediaPostContext;
	expirationDateInEffect: boolean;
	onRenderStatusSpecificDetails: () => JSX.Element;
	overlayLogo: boolean;
	impersonationCtx: Api.IImpersonationContext;
	hideWhiteBackground: boolean;
	logoSize: number;
}

export const DefaultComposerContext = ({
	context,
	expirationDateInEffect,
	onRenderStatusSpecificDetails,
	overlayLogo,
	impersonationCtx,
	hideWhiteBackground,
	logoSize,
}: DefaultComposerContextProps) => {
	return (
		<>
			<PreviewSocialMediaPost
				postTargets={dedupeByProvider(context.postTargets)}
				overlayLogo={overlayLogo}
				postContent={context.postContent}
				postImages={context.postImages}
				impersonationCtx={impersonationCtx}
				hideWhiteBackground={hideWhiteBackground}
				postCreatorUserId={context.selectedCreator?.id}
				logoSize={logoSize}
				onLogoSizeChange={context.setLogoSize}
				templateId={context.template?.id}
			>
				<>
					{expirationDateInEffect ? (
						<div className={css(styleSheet.expirationDateContainer)}>
							<ExpiryIcon fill={warningDark} />
							<span>
								{`This post is available to send until ${moment(context.template.schedule.expirationDate).format(
									'MM/DD/YY'
								)}`}
							</span>
						</div>
					) : null}
				</>
			</PreviewSocialMediaPost>

			{onRenderStatusSpecificDetails()}
		</>
	);
};
